import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { SiteController } from '../../lib/site-controllers';
import { AreaResponse } from '../../lib/areas';
import type { CurfewConfig } from '../../lib/weekly-availability';
import AreaWithPresets from '../areas/AreaWithPresets';
import { Plus, Trash2, Server, RefreshCw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import SiteControllerStateBadge from './SiteControllerStateBadge';
import { PresetResponse, presetsApi } from '@/lib/presets';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Props {
  controllers: SiteController[];
  areasByController?: Map<string, AreaResponse[]>;
  /** Site lighting curfew for preset availability labels. */
  siteCurfew?: CurfewConfig;
  canConfigure: boolean;
  canManage: boolean;
  onCreateController: () => void | Promise<void>;
  onEditController: (controller: SiteController) => void;
  onUpdateController: (controllerId: string, ipAddress: string) => void;
  onDeleteController: (controllerId: string) => void;
  onSyncController: (controllerId: string) => void;
  onCreateArea: (controllerId: string) => void;
  onEditArea: (area: AreaResponse) => void;
  onDeleteArea: (areaId: string, controllerId: string) => void;
  onCreatePreset: (areaId: string) => void;
  onEditPreset: (preset: PresetResponse) => void;
  onDeletePreset?: (presetId: string, areaId: string) => void;
  onSetCurrentPreset: ((areaId: string, presetId: string | null, presetAddress?: number | null) => void) | undefined;
}

export default function SiteControllersPanel({
  controllers,
  areasByController,
  siteCurfew,
  canConfigure,
  onCreateController,
  onEditController,
  onUpdateController,
  onDeleteController,
  onSyncController,
  onCreateArea,
  onEditArea,
  onDeleteArea,
  onCreatePreset,
  onEditPreset,
  onDeletePreset,
}: Props) {
  const [editingIpAddress, setEditingIpAddress] = useState<{ controllerId: string; value: string } | null>(null);
  const [uiAlert, setUiAlert] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: 'Error',
    message: 'Something went wrong',
  });

  const userAreaByController = useMemo(() => {
    const m = new Map<
      string,
      { areaId: string; currentPresetId: string | null } | null
    >();
    controllers.forEach((c) => {
      const areas = areasByController?.get(c.id) ?? [];
      const userArea = areas.find((a) => a.type === 'user') ?? null;
      m.set(
        c.id,
        userArea ? { areaId: userArea.id, currentPresetId: userArea.currentPresetId } : null,
      );
    });
    return m;
  }, [controllers, areasByController]);

  const userAreaPresetQueries = useQueries({
    queries: controllers.map((c) => {
      const userArea = userAreaByController.get(c.id);
      return {
        queryKey: ['presets', 'user-area', userArea?.areaId] as const,
        queryFn: () => presetsApi.getWithFilter({ areaId: userArea!.areaId, type: 'light' }),
        enabled: !!userArea?.areaId,
        staleTime: 30_000,
      };
    }),
  });

  const activeUserLabelByController = useMemo(() => {
    const m = new Map<string, string | null>();
    controllers.forEach((c, i) => {
      const userArea = userAreaByController.get(c.id);
      const presetId = userArea?.currentPresetId;
      if (!presetId) {
        m.set(c.id, null);
        return;
      }
      const presets = userAreaPresetQueries[i]?.data;
      const preset = presets?.find((p) => p.id === presetId);
      const label = preset
        ? preset.userGroupName ?? preset.dashboardName ?? preset.name ?? null
        : null;
      m.set(c.id, label);
    });
    return m;
  }, [controllers, userAreaByController, userAreaPresetQueries]);

  const showErrorDialog = (error: unknown, fallbackMessage: string) => {
    const e = error as { response?: { data?: { error?: string } }; message?: string };
    const message = e.response?.data?.error ?? e.message ?? fallbackMessage;
    setUiAlert({ open: true, title: 'Error', message });
  };

  const handleCreateControllerClick = async () => {
    try {
      await onCreateController();
    } catch (error) {
      showErrorDialog(error, 'Could not open the controller form');
    }
  };

  const handleIpAddressChange = (controllerId: string, value: string) => {
    setEditingIpAddress({ controllerId, value });
  };

  const handleIpAddressBlur = (controller: SiteController) => {
    if (editingIpAddress && editingIpAddress.controllerId === controller.id) {
      const newIpAddress = editingIpAddress.value.trim();
      if (newIpAddress !== (controller.ipAddress || '')) {
        onUpdateController(controller.id, newIpAddress);
      }
      setEditingIpAddress(null);
    }
  };

  const handleIpAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setEditingIpAddress(null);
    }
  };
  return (
    <div className="space-y-6">
      <AlertDialog open={uiAlert.open} onOpenChange={(open) => setUiAlert((p) => ({ ...p, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{uiAlert.title}</AlertDialogTitle>
            <AlertDialogDescription>{uiAlert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Site Controllers</h2>
        {canConfigure && (
          <Button onClick={() => void handleCreateControllerClick()} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Controller
          </Button>
        )}
      </div>
      {controllers && controllers.length > 0 ? (
        controllers.map((controller) => {
          const controllerAreas = areasByController?.get(controller.id) || [];
          const activeUserLabel = activeUserLabelByController.get(controller.id) ?? null;

          return (
            <div key={controller.id} className="space-y-4">
              {/* Controller Info */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Server className="h-5 w-5 text-muted-foreground shrink-0" />
                        <CardTitle className="mb-0">{controller.name}</CardTitle>
                        <SiteControllerStateBadge controller={controller} className="text-xs" />
                      </div>
                      {controller.state === 'warning' && controller.stateDescription?.trim() && (
                        <p className="text-xs text-amber-900 dark:text-amber-200 sm:ml-7 max-w-2xl">
                          {controller.stateDescription}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {activeUserLabel && (
                        <Badge variant="default" className="text-sm font-semibold">
                          User Group: {activeUserLabel}
                        </Badge>
                      )}
                      {canConfigure && (
                        <div className="flex flex-wrap gap-2 justify-end">
                          {controller.ipAddress && (
                            <Button variant="outline" size="sm" onClick={() => onSyncController(controller.id)}>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Sync
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onEditController(controller)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>

                          <Button variant="destructive" size="sm" onClick={() => onDeleteController(controller.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">IP:</span>
                      {canConfigure ? (
                        <Input
                          type="text"
                          value={editingIpAddress?.controllerId === controller.id 
                            ? editingIpAddress.value 
                            : controller.ipAddress || ''}
                          onChange={(e) => handleIpAddressChange(controller.id, e.target.value)}
                          onBlur={() => handleIpAddressBlur(controller)}
                          onKeyDown={handleIpAddressKeyDown}
                          className="h-7 w-48 text-sm"
                          placeholder="Enter IP address"
                        />
                      ) : (
                        <span>{controller.ipAddress || 'Not set'}</span>
                      )}
                    </div>
                    {controller.lastSeen && <p>Last seen: {new Date(controller.lastSeen).toLocaleString()}</p>}
                  </div>
                </CardContent>
              </Card>

              {/* User groups section */}
              <Card>
              <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Controller Groups</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {controllerAreas.filter((area) => area.type === 'user').length > 0 ? (
                    <div className="space-y-4">
                      {controllerAreas.filter((area) => area.type === 'user').map((area) => (
                        <AreaWithPresets
                          key={area.id}
                          area={area}
                          siteCurfew={siteCurfew}
                          canManage={canConfigure}
                          onEditArea={onEditArea}
                          onDeleteArea={(areaId) => onDeleteArea(areaId, controller.id)}
                          onCreatePreset={onCreatePreset}
                          onEditPreset={onEditPreset}
                          onDeletePreset={onDeletePreset}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No user areas configured for this controller</p>
                      {canConfigure && (
                        <Button onClick={() => onCreateArea(controller.id)} size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Area
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Light Areas Section */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Light Areas</CardTitle>
                    {canConfigure && (
                      <Button onClick={() => onCreateArea(controller.id)} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Area
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {controllerAreas.filter((area) => area.type === 'light').length > 0 ? (
                    <div className="space-y-4">
                      {controllerAreas.filter((area) => area.type === 'light').map((area) => (
                        <AreaWithPresets
                          key={area.id}
                          area={area}
                          siteCurfew={siteCurfew}
                          canManage={canConfigure}
                          onEditArea={onEditArea}
                          onDeleteArea={(areaId) => onDeleteArea(areaId, controller.id)}
                          onCreatePreset={onCreatePreset}
                          onEditPreset={onEditPreset}
                          onDeletePreset={onDeletePreset}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No light areas configured for this controller</p>
                      {canConfigure && (
                        <Button onClick={() => onCreateArea(controller.id)} size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Area
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Other Areas Section */}
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Other Areas</CardTitle>
                    {canConfigure && (
                      <Button onClick={() => onCreateArea(controller.id)} size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Area
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {controllerAreas.filter((area) => area.type === 'system' || area.type === 'hidden').length > 0 ? (
                    <div className="space-y-4">
                      {controllerAreas.filter((area) => area.type === 'system' || area.type === 'hidden').map((area) => (
                        <AreaWithPresets
                          key={area.id}
                          area={area}
                          siteCurfew={siteCurfew}
                          canManage={canConfigure}
                          onEditArea={onEditArea}
                          onDeleteArea={(areaId) => onDeleteArea(areaId, controller.id)}
                          onCreatePreset={onCreatePreset}
                          onEditPreset={onEditPreset}
                          onDeletePreset={onDeletePreset}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No other areas configured for this controller</p>
                      {canConfigure && (
                        <Button onClick={() => onCreateArea(controller.id)} size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Area
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>              
            </div>
          );
        })
      ) : (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">No site controllers configured</p>
          <p className="text-sm text-muted-foreground mb-4">Configure controllers to control site areas and presets</p>
        </div>
      )}
    </div>
  );
}
